import { trackPendingMapArt } from '~/client/src/shared/map/mapArtPending'

import { TEXT_BANDS, textBandBake } from './markupTextScale'
import { paintTextMarkup, paintTextMarkupRing } from './textMarkupRaster'
import { mapRatio, textMarkupEntry } from './textMarkupRegistry'

export interface ITextTexture {
  texture: WebGLTexture
  // Layout px at scale 1, as painted (whole device pixels around the box).
  width: number
  height: number
}

interface ICachedTexture extends ITextTexture {
  bytes: number
  lastUsedFrame: number
  lastUsedMs: number
}

const MAX_SIDE_PX = 2048
// Texture memory a map keeps for text before it evicts what the camera has left.
const TEXTURE_BUDGET_BYTES = 32 * 1024 * 1024
// Kept this long after its last draw, so zooming out and back in reuses the bands it left.
const STALE_MS = 2000
// A band crossing repaints every visible text; spread it so no frame pays for all of them.
export const UPGRADE_BUDGET_MS = 4

// One per map: every text layer of a map draws from its GL context, so the mounts share
// the textures. They outlive a style reload and go with the map.
export class TextMarkupTextures {
  private readonly textures = new Map<string, ICachedTexture>()
  private readonly queue = new Map<
    string,
    { id: string; band: number; ring: boolean }
  >()
  private readonly maxSidePx: number
  // Held in mapArtPending while bands are queued, so a capture never shoots a soft band.
  private upgradesDone: (() => void) | null = null
  private frameMs = 0
  private frameSpentMs = 0
  private frameNo = 0
  private evictTimer: ReturnType<typeof setTimeout> | undefined
  private bytes = 0
  // Device px per CSS px of the canvas the textures were painted for.
  private paintedRatio: number | null = null

  public constructor(
    private readonly map: mapboxgl.Map,
    private readonly gl: WebGL2RenderingContext,
    private readonly budgetBytes = TEXTURE_BUDGET_BYTES,
  ) {
    this.maxSidePx = Math.min(MAX_SIDE_PX, gl.getParameter(gl.MAX_TEXTURE_SIZE))
    // The context's objects are gone; mapbox restores the context, not our textures.
    map.on('webglcontextlost', () => {
      this.textures.clear()
      this.bytes = 0
      this.queue.clear()
      this.settleUpgrades()
    })
    // A canvas resized to another pixel density (the window moved to another screen): repaint
    // every text at the new density as it is next drawn.
    map.on('resize', () => {
      if (this.paintedRatio == null || mapRatio(map) === this.paintedRatio) {
        return
      }
      this.textures.forEach(t => gl.deleteTexture(t.texture))
      this.textures.clear()
      this.bytes = 0
      this.dropQueue()
      this.paintedRatio = null
    })
    // Every text layer of the map draws between these two, so they share one frame's budget.
    map.on('renderstart', () => {
      this.frameMs = performance.now()
      this.frameSpentMs = 0
    })
    map.on('render', () => this.frameNo++)
    map.on('moveend', () => this.evict())
    // remove() never calls a custom layer's onRemove and unhooks its own contextlost first, so
    // only its 'remove' can settle what a removed map still had queued.
    map.once('remove', () => {
      clearTimeout(this.evictTimer)
      this.dropQueue()
    })
  }

  public get hasQueued(): boolean {
    return this.queue.size > 0
  }

  // The exact band when painted; else the nearest painted band of the same text, drawn
  // soft until drain() paints the exact one; a text never painted is painted now.
  // `ring`: the copy-select ring around the text instead of the text.
  public textureFor(
    id: string,
    band: number,
    ring = false,
  ): ITextTexture | null {
    const exact = this.textures.get(keyOf(id, band, ring))
    if (exact) return this.use(exact)
    for (let d = 1; d < TEXT_BANDS.length; d++) {
      for (const near of [band - d, band + d]) {
        const fallback = this.textures.get(keyOf(id, near, ring))
        if (fallback) {
          this.use(fallback)
          this.queue.set(keyOf(id, band, ring), { id, band, ring })
          if (!this.upgradesDone) {
            trackPendingMapArt(
              new Promise<void>(resolve => (this.upgradesDone = resolve)),
            )
          }
          return fallback
        }
      }
    }
    const start = performance.now()
    const painted = this.paint(id, band, ring)
    this.frameSpentMs += performance.now() - start
    return painted && this.use(painted)
  }

  // Paints queued bands within this frame's share of the budget. True when any was painted.
  public drain(band: number): boolean {
    if (!this.queue.size) return false
    const now = performance.now()
    const end = now + UPGRADE_BUDGET_MS - this.frameSpentMs
    let painted = false
    for (const [key, item] of this.queue) {
      if (performance.now() >= end) break
      this.queue.delete(key)
      if (item.band === band && this.paint(item.id, item.band, item.ring)) {
        painted = true
      }
    }
    this.frameSpentMs += Math.max(0, performance.now() - now)
    if (!this.queue.size) {
      this.settleUpgrades()
      // The soft bands the upgrades replaced; at rest no moveend follows to drop them.
      this.evictWhenStale()
    }
    return painted
  }

  // Only a drawing layer drains the queue; with none left on the map it would never empty.
  public dropQueue(): void {
    this.queue.clear()
    this.settleUpgrades()
  }

  // Over budget, drops the least recently drawn textures of any band not drawn for STALE_MS.
  // What the last frame drew always stays, so a dense view can sit over budget. Textures left
  // only for being recent are checked again once they are stale, unless the camera moves
  // again: its moveend checks them. Asked for again, a text draws a nearby band soft or is
  // repainted at once.
  private evict(isRecheck = false): void {
    clearTimeout(this.evictTimer)
    if (this.bytes <= this.budgetBytes) return
    const now = performance.now()
    // frameNo is the frame still to draw, so frameNo - 1 is the one on screen.
    const undrawn = [...this.textures]
      .filter(([, t]) => t.lastUsedFrame < this.frameNo - 1)
      .sort(([, a], [, b]) => a.lastUsedMs - b.lastUsedMs)
    for (const [key, t] of undrawn) {
      if (this.bytes <= this.budgetBytes) return
      if (now - t.lastUsedMs < STALE_MS) {
        this.evictWhenStale()
        return
      }
      this.gl.deleteTexture(t.texture)
      this.textures.delete(key)
      this.bytes -= t.bytes
    }
    // A jump's moveend comes before its first frame, so the view it left still counts as drawn:
    // look once more after the new view has drawn.
    if (!isRecheck && this.bytes > this.budgetBytes) this.evictWhenStale()
  }

  private evictWhenStale(): void {
    clearTimeout(this.evictTimer)
    this.evictTimer = setTimeout(() => {
      if (!this.map.isMoving()) this.evict(true)
    }, STALE_MS)
  }

  private use(texture: ICachedTexture): ICachedTexture {
    texture.lastUsedFrame = this.frameNo
    texture.lastUsedMs = this.frameMs
    return texture
  }

  private settleUpgrades(): void {
    this.upgradesDone?.()
    this.upgradesDone = null
  }

  private paint(
    id: string,
    band: number,
    ring: boolean,
  ): ICachedTexture | null {
    const key = keyOf(id, band, ring)
    const cached = this.textures.get(key)
    if (cached) return cached
    const entry = textMarkupEntry(id)
    if (!entry) return null
    const gl = this.gl
    this.paintedRatio = mapRatio(this.map)
    const requested = this.paintedRatio * textBandBake(band)
    const { canvas, ratio } = ring
      ? paintTextMarkupRing(entry.layout, requested, this.maxSidePx)
      : paintTextMarkup(entry.layout, entry.spec, requested, this.maxSidePx)
    const texture = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas)
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    const painted = {
      texture,
      width: canvas.width / ratio,
      height: canvas.height / ratio,
      bytes: canvas.width * canvas.height * 4,
      lastUsedFrame: this.frameNo,
      lastUsedMs: this.frameMs,
    }
    this.textures.set(key, painted)
    this.bytes += painted.bytes
    return painted
  }
}

const keyOf = (id: string, band: number, ring: boolean): string =>
  `${id}${ring ? '-ring' : ''}@${band}`

const byMap = new WeakMap<mapboxgl.Map, TextMarkupTextures>()

export function textMarkupTextures(
  map: mapboxgl.Map,
  gl: WebGL2RenderingContext,
): TextMarkupTextures {
  if (!byMap.has(map)) byMap.set(map, new TextMarkupTextures(map, gl))
  return byMap.get(map)
}
